import { createContext, ReactNode, use, useMemo } from "react";
import { usePermissionContext } from "./PermissionContext";
import packageJson from "../../package.json";
import {
  Activity,
  BookMarked,
  BookOpen,
  CalendarClock,
  DraftingCompass,
  FileSpreadsheet,
  FolderOpen,
  FolderPlus,
  Grid3X3,
  MessageSquareShare,
  Milestone,
  Microscope,
  TableProperties,
  Tag,
  Target,
  Users,
  Workflow,
} from "lucide-react";

type NavGroup = {
  title: string;
  items: {
    title: string;
    url: string;
    icon: any;
    action: string; // Permission action
    resource: string; // Permission resource
    badge?: string;
    external?: boolean;
  }[];
};

export interface DocumentationPage {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  route: string;
  resource: string; // Permission resource
  action: string; // Permission action
  docsPath: string;
}

interface ApplicationContextType {
  allDocumentationPages: DocumentationPage[];
  availableSidebarNavigationOptions: NavGroup[];
  documentationPagesForLandingsPage: DocumentationPage[];
}

const ApplicationContext = createContext<ApplicationContextType>({
  allDocumentationPages: [],
  availableSidebarNavigationOptions: [],
  documentationPagesForLandingsPage: [],
});

const navGroups: NavGroup[] = [
  {
    title: "General",
    items: [
      {
        title: "Projects",
        url: "/projects",
        icon: FolderOpen,
        action: "readAll",
        resource: "projects",
      },
      {
        title: "Request Planning",
        url: "/request-planning",
        icon: CalendarClock,
        action: "readAll",
        resource: "measurement_requests",
      },
    ],
  },
  {
    title: "Admin",
    items: [
      {
        title: "Master Specs",
        url: "/master-specs",
        icon: FileSpreadsheet,
        action: "readAll",
        resource: "master_spec_lists",
      },
      {
        title: "Users",
        url: "/users",
        icon: Users,
        action: "create",
        resource: "users",
      },
      {
        title: "Systems",
        url: "/systems",
        icon: Microscope,
        action: "readAll",
        resource: "measurement_systems",
      },
    ],
  },
  {
    title: "Help Center",
    items: [
      {
        title: "Documentation",
        url: "/docs",
        icon: BookOpen,
        action: "readAll",
        resource: "users",
      },
      {
        title: "Feedback & Report",
        url: "https://imec.atlassian.net/servicedesk/customer/portal/109",
        icon: MessageSquareShare,
        action: "readAll",
        resource: "users",
        external: true,
      },
    ],
  },
  {
    title: "App Info",
    items: [
      {
        title: "Changelog",
        url: `/changelog/v${packageJson.version}`,
        badge: `v${packageJson.version}`,
        icon: Milestone,
        action: "readAll",
        resource: "users",
      },
    ],
  },
];

const scenarios: DocumentationPage[] = [
  {
    id: "create-project",
    title: "Create New Project",
    description:
      "Start a new project to organize your masks, test labels, and measurement requests",
    icon: FolderPlus,
    route: "/projects?modal=create",
    resource: "projects",
    action: "create",
    docsPath: "/docs/create-project",
  },
  {
    id: "create-mask",
    title: "Create New Mask in Project",
    description: "Add a new mask to an existing project for organizing test structures",
    icon: Target,
    route: "/projects",
    resource: "masks",
    action: "create",
    docsPath: "/docs/create-mask",
  },
  {
    id: "mask-workflow",
    title: "Mask Workflow",
    description: "Complete guide to preparing masks for measurement requests",
    icon: Workflow,
    route: "/projects",
    resource: "masks",
    action: "create",
    docsPath: "/docs/mask-workflow",
  },
  {
    id: "create-test-label",
    title: "Create New Test Label in Mask",
    description: "Define test labels within a mask to specify measurement points and conditions",
    icon: Tag,
    route: "/test-labels?modal=create",
    resource: "test-labels",
    action: "create",
    docsPath: "/docs/create-test-label",
  },
  {
    id: "create-project-spec",
    title: "Create Project Spec for Mask",
    description: "Define project specifications and requirements for mask testing",
    icon: TableProperties,
    route: "/spec-lists?modal=create",
    resource: "spec_lists",
    action: "create",
    docsPath: "/docs/create-project-spec",
  },
  {
    id: "create-measurement-request",
    title: "Create Measurement Request",
    description: "Request measurements for existing test labels with specific parameters",
    icon: DraftingCompass,
    route: "/measurement-requests?modal=create",
    resource: "measurement-requests",
    action: "create",
    docsPath: "/docs/create-measurement-request",
  },
  {
    id: "plan-measurement-requests",
    title: "Plan Measurement Requests",
    description: "Schedule and organize measurement requests for efficient testing workflows",
    icon: CalendarClock,
    route: "/request-planning",
    resource: "measurement-requests",
    action: "update",
    docsPath: "/docs/plan-measurement-requests",
  },
];

// These documentation pages are only visible on /documentation and
// not on the landingspage
const documentationPagesOnlyOnDocumentationIndex: DocumentationPage[] = [
  {
    id: "custom-spec-list-csv",
    title: "Custom Spec List CSV Format",
    description: "Reference guide for creating custom spec lists via CSV upload",
    docsPath: "/docs/custom-spec-list-csv",
    resource: "",
    action: "",
    icon: TableProperties,
    route: "",
  },
  {
    id: "chip-pos-file-format",
    title: "Chip Pos File Format",
    description: "Reference guide for the .chip_pos ASCII file format used for die selection",
    docsPath: "/docs/chip-pos-file-format",
    resource: "",
    action: "",
    icon: Grid3X3,
    route: "",
  },
  {
    id: "glossary",
    title: "Glossary",
    description: "Key terms, concepts, and definitions used in Photon Flow",
    docsPath: "/docs/glossary",
    resource: "",
    action: "",
    icon: BookMarked,
    route: "",
  },
  {
    id: "pholis",
    title: "Pholis Service",
    description:
      "How the Pholis analysis service works: data flow, blob storage inputs and outputs, and Grafana visualization",
    docsPath: "/docs/pholis",
    resource: "",
    action: "",
    icon: Activity,
    route: "",
  },
];

interface ApplicationContextProviderProps {
  children: ReactNode;
}
export const ApplicationContextProvider = (props: ApplicationContextProviderProps) => {
  const { hasPermission } = usePermissionContext();

  const documentationPagesForLandingsPageForCurrentUser = useMemo(() => {
    return scenarios.filter((scenario) => hasPermission(scenario.resource, scenario.action));
  }, [hasPermission]);

  const availableSidebarNavigationOptionsForCurrentUser = useMemo(() => {
    const filteredNavItemsWithoutSufficientPermissions = navGroups.map((ng) => ({
      ...ng,
      items: ng.items.filter((item) => hasPermission(item.resource, item.action)),
    }));

    const filteredNavGroupsWithoutItems = filteredNavItemsWithoutSufficientPermissions.filter(
      (ng) => ng.items.length > 0,
    );

    return filteredNavGroupsWithoutItems;
  }, [hasPermission]);

  const value = useMemo(() => {
    return {
      allDocumentationPages: [...scenarios, ...documentationPagesOnlyOnDocumentationIndex],
      availableSidebarNavigationOptions: availableSidebarNavigationOptionsForCurrentUser,
      documentationPagesForLandingsPage: documentationPagesForLandingsPageForCurrentUser,
    };
  }, [
    documentationPagesForLandingsPageForCurrentUser,
    availableSidebarNavigationOptionsForCurrentUser,
  ]);

  return <ApplicationContext value={value}>{props.children}</ApplicationContext>;
};

/**
 * Hook to use application context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useApplicationContext = (): ApplicationContextType => {
  const context = use(ApplicationContext);

  if (!context) {
    throw new Error("useApplicationContext must be used within a ApplicationContextProvider");
  }

  return context;
};
